/**
 * The app-wide TanStack `QueryClient` factory (E10-F01-T04).
 *
 * One place that owns the cache defaults so every entry point (the SPA mount in
 * `main.tsx`, and any test that needs an isolated client) builds the client the
 * same way. Centralised here rather than inlined at the mount so the defaults
 * are a stable contract and can't drift per call-site.
 *
 * Defaults chosen for this app (architecture.md §5 "Состояние сервера — TanStack
 * Query, заменяет ручной app.state + localStorage"):
 *   - `staleTime` 30s — the lists (projects/clients/emails) are not real-time;
 *     a short window avoids a refetch storm on every focus/remount while still
 *     refreshing quickly. Mutations invalidate explicitly (queries.ts), so a
 *     write is reflected immediately regardless of this window.
 *   - `retry` 1 — the client already converts a network failure into a typed
 *     `ApiResultError` (it never throws a raw fetch error), so one retry covers a
 *     transient blip without masking a real, surfaced error behind long backoff.
 *   - `refetchOnWindowFocus` off — parity with the legacy SPA, which refreshed
 *     on explicit navigation/action, not on tab focus.
 */
import { QueryClient } from '@tanstack/react-query';

/** Build a fresh `QueryClient` with the app's cache defaults. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}
